import React, { ChangeEvent, useContext, useEffect, useState } from "react";
import { Grid, FormControlLabel, Checkbox, Alert, Box, Chip, Typography } from "@mui/material";
import Button from "components/shared/Button/Button";
import SimpleGrid from "components/shared/SimpleGrid/SimpleGrid";
import {
  BasicText,
  ThresholdContainer,
  ThresholdHeaderText,
  StyledFormGroup,
  StyledButtonDiv,
  StyledGrid,
  StyledStackContainer
} from "../Thresholds/Thresholds.styles";
import { EncounterTimerDisplay } from "components/shared/EncounterTimer/EncounterTimer";
import { useParams } from "react-router";
import Session from "helpers/session";
import {
  AddEncounterEvent,
  associateReadingType
} from "helpers/variables";
import { GlobalContext } from "context/Provider";
import { ThresholdInterface } from "context/initialState/Threshold";
import { useTranslation } from "react-i18next";
import {
  uAbsoluteThreshold,
  uModerateThreshold,
  sThresholdText,
  uHighThreshold
} from "helpers/translationStringConstants";
import {
  generateInputs,
  getThresholds,
  setThresholds,
  removeThresholds,
  GenerateTableHeaders,
  generateList,
  updateThreshold
} from "./Thresholds.utility";
import {
  HiddenFieldsChips,
  useSpirometryVisibility,
  spirometryLabels,
  defaultSpirometryVisibility
} from "views/PatientBoard/microViews/History/Measurements/Measurements.utils";

interface AttributeProps {
    type: string;
}

export default function Thresholds({type}: AttributeProps) {
  const { memberId } = useParams();
  const { threshold, thresholdDispatch, provider, encounterTimer, settings, settingsDispatch } =
    useContext(GlobalContext);
  const { t } = useTranslation();
  const [highChecked, setHighChecked] = useState<boolean>(false);
  const [lowChecked, setLowChecked] = useState<boolean>(false);
  const [highThreshold, setHighThreshold] = useState<ThresholdInterface>();
  const [moderateThreshold, setModerateThreshold] =
    useState<ThresholdInterface>();
  const [applyDisabled, setApply] = useState<boolean>(true);
  const [showSuccess, setSuccess] = useState<string>("");
  const [totalThresholdList, setTotalThresholdList] = useState<number>(0);
  const [thresholdList, setThresholdList] = useState([]);
  const { visibility, hiddenFields, updateVisibility } = useSpirometryVisibility();

  const paginationAction = (itemsPerPage?: number, currentPage?: number) => {
    const paramString = `limit=${itemsPerPage}&page=${currentPage}`;
    getThresholds(memberId, type, paramString).then((thresholds) => {
      setThresholdList(thresholds);
    });
  };

  const typeCast = (type: string) => {
    switch(type) {
      case "Blood Pressure":
        return "bp";
      case "Blood Glucose":
        return "bgl";
      case "Heart Rate":
        return "hrt";
      case "Oxygen Saturation":
        return "spo2";
      case "Spirometry":
        return "spir";
      case "Temperature":
        return "temp";
      case "Weight":
        return "wt";
      default:
        return type.toLowerCase();
    }
  };

  const hasLowHigh = (obj: any): boolean => {
    if (!obj) {
      return false;
    }
    if (Object.keys(obj).length === 0) {
      return false;
    }

    return Object.entries(obj).every(([readingKey, reading]: [string, any]) => {
      if (!reading) return false;

      const hasLow =
        reading.low !== undefined &&
        reading.low !== "" &&
        reading.low !== null &&
        !isNaN(Number(reading.low));

      const hasHigh =
        reading.high !== undefined &&
        reading.high !== "" &&
        reading.high !== null &&
        !isNaN(Number(reading.high));

      return hasLow && hasHigh;
    });
  };

  useEffect(() => {
    thresholdDispatch({
      type: "UPDATE",
      payload: { high: {}, moderate: {} }
    });

    getThresholds(memberId, type).then((thresholds) => {
      setTotalThresholdList(thresholds.length);
      paginationAction(5, 0);
      if (thresholds.length > 0) {
        const high = thresholds.filter((T: ThresholdInterface) => {
          return T.breachType === "high";
        });
        const low = thresholds.filter((T: ThresholdInterface) => {
          return T.breachType === "moderate";
        });
        setHighChecked(high.length > 0);
        setLowChecked(low.length > 0);
        setHighThreshold(high[0]);
        setModerateThreshold(low[0]);
        if (high.length > 0) {
          setHighChecked(true);
          thresholdDispatch({
            type: "UPDATE",
            payload: { high: high[0].value }
          });
        }
        if (low.length > 0) {
          setLowChecked(true);
          thresholdDispatch({
            type: "UPDATE",
            payload: { moderate: low[0].value }
          });
        }
      }
    });
  }, [type]);

  const submitThresholds = async () => {
    const promiseArray: Promise<any>[] = [];
    const isLowValid = !lowChecked || hasLowHigh(threshold?.moderate);
    const isHighValid = !highChecked || hasLowHigh(threshold?.high);

    if (!isLowValid || !isHighValid) {
      setApply(true);
      setSuccess("fail");
      setTimeout(() => setSuccess(""), 6000);
      return;
    }
    if (lowChecked) {
      // @ts-ignore
      if(moderateThreshold) {
        promiseArray.push(updateThreshold({
          ...moderateThreshold,
          value: threshold?.moderate,
        }));
      } else {
        const newSource: any = {};
        newSource.memberId = memberId;
        newSource.coordinator = provider?.loggedInClient.id;
        newSource.breachType = "moderate";
        newSource.value = threshold?.moderate;
        newSource.readingType = typeCast(type);
        promiseArray.push(
          setThresholds(newSource).then((newThreshold) => {
            setModerateThreshold(newThreshold);
          })
        );
      }
    }
    if (highChecked) {
      // @ts-ignore
      if(highThreshold) {
        promiseArray.push(updateThreshold({
          ...highThreshold,
          value: threshold?.high
        }));
      } else {
        const newSource: any = {};
        newSource.memberId = memberId;
        newSource.coordinator = provider?.loggedInClient.id;
        newSource.breachType = "high";
        newSource.value = threshold?.high;
        newSource.readingType = typeCast(type);
        promiseArray.push(
          setThresholds(newSource).then((newThreshold) => {
            setHighThreshold(newThreshold);
          })
        );
      }
    }

    if (!lowChecked && moderateThreshold) {
      // @ts-ignore
      promiseArray.push(removeThresholds(moderateThreshold));
      setModerateThreshold(undefined);
    }
    if (!highChecked && highThreshold) {
      // @ts-ignore
      promiseArray.push(removeThresholds(highThreshold));
      setHighThreshold(undefined);
    }

    try {
      await Promise.all(promiseArray);
      setApply(true);
      setSuccess("success");
      paginationAction();
      setTimeout(() => setSuccess(""), 6000);
    } catch (err) {
      setApply(true);
      setSuccess("fail");
      setTimeout(() => setSuccess(""), 6000);
    }
    const encounterEvent = AddEncounterEvent(
      "profile change",
      "Threshold updated"
    );
    const duration = encounterTimer?.current?.duration ?? 0;
    Session.updateSessionEncounter(
      window.sessionStorage.getItem("member") || "",
      encounterEvent,
      duration.toString() || ""
    );
  };

  const toggleLow = () => {
    setApply(false);
    setLowChecked(!lowChecked);
  };
  const toggleHigh = () => {
    setApply(false);
    setHighChecked(!highChecked);
  };

  const handleChange = (event: ChangeEvent<HTMLElement>) => {
    setSuccess("");
    setApply(false);
  };

  return (
    <Grid container>
      <Grid item xs={0.4} sm={1} md={1.5} lg={0.5} />
      <Grid item xs={10.8} sm={11} md={10.2} lg={11.4}>
        <StyledStackContainer>
          <EncounterTimerDisplay/>
          {type === "Spirometry" && (
            <HiddenFieldsChips
              hiddenFields={hiddenFields}
              labels={spirometryLabels}
              sx={{ pl: 2 }}
              onRestore={(field) =>
                updateVisibility({
                  ...visibility,
                  [field]: true,
                })
              }
            />
          )}
          <BasicText>{t(sThresholdText)}</BasicText>
          <ThresholdContainer>
            <ThresholdHeaderText>{t(uAbsoluteThreshold)}</ThresholdHeaderText>
            <StyledGrid container>
              <Grid item lg={6}>
                <StyledFormGroup>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={lowChecked}
                        onChange={(e) => {
                          toggleLow();
                          handleChange(e);
                        }}
                      />
                    }
                    label={t(uModerateThreshold)}
                  />
                </StyledFormGroup>
                {generateInputs(
                  associateReadingType(type),
                  "moderate",
                  !lowChecked,
                  (e: any) => {
                    handleChange(e);
                  },
                  visibility
                )}
              </Grid>
              <Grid item md={6.1} lg={6}>
                <StyledFormGroup>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={highChecked}
                        onChange={(e) => {
                          toggleHigh();
                          handleChange(e);
                        }}
                      />
                    }
                    label={t(uHighThreshold)}
                  />
                </StyledFormGroup>
                {generateInputs(
                  associateReadingType(type),
                  "high",
                  !highChecked,
                  (e: any) => {
                    handleChange(e);
                  },
                  visibility
                )}
              </Grid>
              <StyledButtonDiv>
                <Button
                  variant="success"
                  onClick={submitThresholds}
                  disabled={applyDisabled}
                >
                  Apply
                </Button>
              </StyledButtonDiv>
            </StyledGrid>
          </ThresholdContainer>
          {showSuccess === "success" && (
            <Alert severity="success">
              Your configuration changes have been saved.
            </Alert>
          )}
          {showSuccess === "fail" && (
            <Alert severity="error">
              Failed to save thresholds. Please try again.
            </Alert>
          )}
          <SimpleGrid
            // @ts-ignore
            headers={GenerateTableHeaders(type, visibility)}
            body={generateList(thresholdList, visibility)}
            paginationAction={paginationAction}
            totalList={totalThresholdList}
          />
        </StyledStackContainer>
      </Grid>
    </Grid>
  );
}
